import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Radio, CheckCircle2, AlertTriangle, RefreshCw, KeyRound, Mic } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mason`;

// Admin → MASON: agent id, tool registration on ElevenLabs, prompt snippet.
export default function MasonPanel() {
  const [st, setSt] = useState(null);
  const [agentId, setAgentId] = useState('');
  const [busy, setBusy] = useState('');
  const load = () => axios.get(`${API}/admin/status`).then(r => { setSt(r.data); setAgentId(r.data.agent_id || ''); }).catch(e => toast.error(e.response?.data?.detail || 'Could not load MASON status'));
  useEffect(() => { load(); }, []);
  if (!st) return null;
  const registered = st.tool_names.filter(n => st.registered?.[n]);
  const saveAgent = async () => {
    setBusy('agent');
    try { await axios.put(`${API}/admin/settings`, { agent_id: agentId }); toast.success('Agent id saved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setBusy(''); }
  };
  const register = async () => {
    setBusy('register');
    try {
      const r = await axios.post(`${API}/admin/register`, { base_url: window.location.origin });
      toast.success(`${r.data.tools.length} tools on "${r.data.agent_name || 'MASON'}" — ${r.data.tools.filter(t => t.action === 'created').length} new, ${r.data.tools.filter(t => t.action === 'updated').length} updated`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 9000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Registration failed'); } finally { setBusy(''); }
  };
  const rotate = async () => {
    if (!window.confirm('Rotate the secret MASON uses to call Ops? The tools stop working until you register again.')) return;
    setBusy('rotate');
    try { await axios.post(`${API}/admin/rotate-secret`); toast.success('Secret rotated — register the tools again'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 space-y-5" data-testid="mason-panel">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-2xl bg-slate-900 text-emerald-300 flex items-center justify-center"><Radio className="w-5 h-5" /></span>
        <div>
          <h2 className="text-lg font-extrabold text-slate-900">MASON — AI dispatcher (ElevenLabs)</h2>
          <p className="text-xs text-slate-500">Ops gives MASON four read-only tools: who's available, a guy's next shift, hours last week, who's on a job.</p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className={`rounded-xl border px-3 py-2 text-sm flex items-center gap-2 ${st.has_api_key ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="mason-key-status">
          <KeyRound className="w-4 h-4" /> ElevenLabs API key: <b>{st.has_api_key ? 'set' : 'missing — ask E1 to add ELEVENLABS_API_KEY'}</b>
        </div>
        <div className={`rounded-xl border px-3 py-2 text-sm flex items-center gap-2 ${registered.length === st.tool_names.length ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-slate-50 border-slate-200 text-slate-700'}`} data-testid="mason-tools-status">
          {registered.length === st.tool_names.length ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          Tools on the agent: <b>{registered.length} / {st.tool_names.length}</b>{st.registered_at ? <span className="text-xs opacity-70">· {new Date(st.registered_at).toLocaleString()}</span> : null}
        </div>
      </div>
      {st.base_url && (
        <div className={`rounded-xl border px-3 py-2 text-sm ${st.base_url.includes('preview.emergentagent.com') ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`} data-testid="mason-pointing-at">
          MASON reads from <b className="font-mono">{st.base_url}</b>{st.base_url.includes('preview.emergentagent.com') ? ' — that is the PREVIEW copy (old data). The live site re-points MASON to itself automatically when it starts; or click Register from the live site.' : ' (live data).'}
        </div>
      )}
      <div className={`rounded-xl border px-3 py-2 text-sm ${st.registered_spec_version === st.spec_version ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="mason-identity-status">
        <b>Knows who's talking:</b> when someone opens "Talk to MASON" inside Ops, their login (name + role) rides along on every question — MASON greets them by name, "when do I work next" just works, and hours follow their Ops role. Phone callers are still asked who they are.
        {st.registered_spec_version !== st.spec_version && <span className="block mt-1 font-semibold">Re-register the tools once (from the live site) to switch this on — the live site also does it by itself on its next start.</span>}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600 flex-1 min-w-[260px]">ElevenLabs agent id
          <input value={agentId} onChange={e => setAgentId(e.target.value)} className="block w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono text-slate-900" data-testid="mason-agent-id" />
        </label>
        <button onClick={saveAgent} disabled={busy === 'agent' || agentId === st.agent_id} className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-40" data-testid="mason-save-agent">Save</button>
      </div>

      <div className="rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-slate-900">Tools</p>
        <ul className="mt-1 text-sm text-slate-700 space-y-0.5">
          {st.tool_names.map(n => (
            <li key={n} className="flex items-center gap-2" data-testid={`mason-tool-${n}`}>
              {st.registered?.[n] ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="w-4 h-4 rounded-full border border-slate-300 inline-block" />}
              <span className="font-mono">{n}</span>
              <span className="text-xs text-slate-400">→ {window.location.origin}/api/mason/tools/{n.replace('ops_', '').replace(/_/g, '-')}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={register} disabled={!st.has_api_key || busy === 'register'} className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-bold disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="mason-register">
            <RefreshCw className={`w-4 h-4 ${busy === 'register' ? 'animate-spin' : ''}`} /> {registered.length ? 'Re-register tools on MASON' : 'Register tools on MASON'}
          </button>
          <button onClick={rotate} disabled={busy === 'rotate'} className="h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold disabled:opacity-40" data-testid="mason-rotate">Rotate secret ({st.secret_preview})</button>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">The live site points MASON at itself automatically the first time anyone opens the dashboard there; this button does the same by hand (from whichever address you click it on). Preview never re-points MASON. Each call carries a secret header only Ops and ElevenLabs know.</p>
      </div>

      <PromptEditor />
    </div>
  );
}

// System prompt + greeting, read from and written straight to the ElevenLabs agent.
function PromptEditor() {
  const [p, setP] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [first, setFirst] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/admin/prompt`).then(r => { setP(r.data); setPrompt(r.data.prompt); setFirst(r.data.first_message); }).catch(e => toast.error(e.response?.data?.detail || 'Could not read MASON\'s prompt'));
  useEffect(() => { load(); }, []);
  if (!p) return null;
  const dirty = prompt !== p.prompt || first !== p.first_message;
  const knowsCaller = prompt.includes('{{caller_name}}');
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/admin/prompt`, { prompt, first_message: first }); toast.success('Saved onto the MASON agent — it applies to the next call'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };
  const useRecommended = () => { setPrompt(p.recommended_prompt); setFirst(p.recommended_first_message); toast.info('Recommended prompt loaded — hit Save to apply'); };
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid="mason-prompt-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5"><Mic className="w-4 h-4" /> MASON's system prompt &amp; greeting</p>
          <p className="text-[11px] text-slate-500">Edited here, saved straight onto the ElevenLabs agent{p.llm ? ` (${p.llm})` : ''}. {p.saved_at ? `Last saved from Ops ${new Date(p.saved_at).toLocaleString()} by ${p.saved_by}.` : 'Not saved from Ops yet.'}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={useRecommended} className="h-8 px-2.5 rounded-lg border border-slate-300 text-xs font-semibold" data-testid="mason-prompt-recommended">Use recommended</button>
          <button onClick={save} disabled={!dirty || busy} className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold disabled:opacity-40" data-testid="mason-prompt-save">{busy ? 'Saving…' : 'Save to MASON'}</button>
        </div>
      </div>
      <div className={`rounded-lg px-3 py-2 text-xs ${knowsCaller ? 'bg-emerald-50 text-emerald-900' : 'bg-rose-50 text-rose-900'}`} data-testid="mason-prompt-identity-check">
        {knowsCaller ? 'This prompt reads the caller\'s Ops login ({{caller_name}} / {{caller_role}} / {{caller_first}}) — MASON knows who it is talking to.' : 'This prompt has no {{caller_name}} line — MASON will not know who is logged in. Use the recommended prompt or add the "Who you are talking to" section back.'}
      </div>
      <label className="block text-xs text-slate-600">First thing MASON says
        <input value={first} onChange={e => setFirst(e.target.value)} className="block w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900" data-testid="mason-first-message" />
        <span className="text-[11px] text-slate-400">{'{{caller_first}}'} becomes the logged-in person's first name, and nothing on a phone call.</span>
      </label>
      <label className="block text-xs text-slate-600">System prompt
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={22} spellCheck={false}
          className="block w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-[12px] leading-relaxed font-mono text-slate-900" data-testid="mason-prompt-text" />
      </label>
    </div>
  );
}
